import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageConfig from '@/component/layout/PageConfig';
import SearchBar from '@/component/input/SearchBar';
import BorderButton from '@/component/button/BorderButton';
import SolidButton from '@/component/button/SolidButton';
import Pagination from '@/component/pagination/Pagination';
import BCGUploadModal, { BCGUploadModalRef } from '@/component/mentalHealth/BCGUploadModal';
import BCGSessionModal, { BCGSessionModalRef } from '@/component/mentalHealth/BCGSessionModal';
import useQueryGetUsersSimple, { IUser } from '@/api/user/getUsersSimple';
import useDebounce from '@/util/hook/useDebounce';
import { mentalHealthStore } from '@/store/MentalHealthStore';
import { isEmptyArray, isNotEmptyArray } from '@/util/array';
import { useGetMindHealthSessions, apiGetSessionBCGData, MindHealthSession } from '@/api/mentalHealth/mindHealthSession';
import { analyzeBCGData } from '@/util/bcg';
import { AnalysisParams, MentalHealthReport } from '@/util/bcg/types';
import { useUserSessionCounts } from '@/hooks/useUserSessionCounts';
import { SymptomPredictionCard } from '@/component/mentalHealth/SymptomPredictionCard';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  Filler
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import IcLoading from '@/assets/ic_loading.svg?react';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  Filler
);

const PAGE_SIZE = 10;

// 사용자별 건강 데이터 인터페이스
interface UserHealthData extends IUser {
  healthScore?: number;
  healthStatus?: string;
  stressLevel?: string;
  lastAnalysisDate?: string;
  age?: number;
  hasAnalysisData: boolean;
  hasBackendData?: boolean; // 백엔드에 세션 데이터가 있는지
  sessionCount?: number; // 백엔드 세션 개수
}

interface RecentAnalysis {
  id: string | number;
  userId: number;
  userName: string;
  analysisDate: string;
  filename: string;
  report?: MentalHealthReport;
  isFromBackend: boolean;
  sessionId?: string;
  session?: MindHealthSession;
}

interface LatestDataCache {
  [key: number]: {
    healthScore?: number;
    healthStatus?: string;
    stressLevel?: string;
  };
}


const MentalHealthDashboard: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const uploadModalRef = useRef<BCGUploadModalRef>(null);
  const sessionModalRef = useRef<BCGSessionModalRef>(null);

  const [search, setSearch] = useState('');
  const [userId, setUserId] = useState(Number(searchParams.get('id')) || 0);
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get('page')) || 1);
  const [latestDataCache, setLatestDataCache] = useState<LatestDataCache>({});

  const debouncedSearchText = useDebounce(search, 500);

  // 전체 회원 데이터 가져오기 (Simple API)
  const { data: allUsers, isLoading } = useQueryGetUsersSimple();

  // 선택된 사용자의 세션 목록 가져오기 (COMPLETED 상태만)
  const { data: sessionsData, refetch: refetchSessions } = useGetMindHealthSessions(
    userId || 0,
    { status: 'COMPLETED', pageSize: 50, sortBy: 'startTime:desc' },
    userId > 0
  );

  // 검색 필터링 및 가나다순 정렬
  const filteredUsers = useMemo(() => {
    if (!allUsers) return [];

    let filtered = allUsers;

    // 검색어 필터링
    if (debouncedSearchText) {
      filtered = filtered.filter(user =>
        user.name.toLowerCase().includes(debouncedSearchText.toLowerCase())
      );
    }

    // 가나다순 정렬 (한글 이름 기준)
    return filtered.sort((a, b) => a.name.localeCompare(b.name, 'ko-KR'));
  }, [allUsers, debouncedSearchText]);


  // 전체 사용자의 세션 개수 조회 (분석 완료된 회원 수 계산용 및 정렬용)
  const allUserIds = useMemo(() => filteredUsers?.map(u => u.id) || [], [filteredUsers]);
  const { sessionCounts: allSessionCounts, isLoading: isAllSessionCountsLoading } = useUserSessionCounts(allUserIds);

  // 회원 데이터와 건강 데이터 합치기 (localStorage + 백엔드 데이터) 및 정렬
  const usersWithHealth = useMemo(() => {
    if (!filteredUsers) return [];

    const allReports = mentalHealthStore.getAllReports();

    const mappedUsers = filteredUsers.map((user): UserHealthData => {
      // localStorage에서 최신 리포트 찾기 (날짜 내림차순 정렬)
      const userReports = allReports
        .filter(r => r.userId === user.id)
        .sort((a, b) => new Date(b.analysisDate).getTime() - new Date(a.analysisDate).getTime());

      const latestReport = userReports[0];

      // 백엔드 세션 데이터 확인
      const sessionInfo = allSessionCounts.get(user.id);
      const hasBackendData = sessionInfo?.hasData || false;
      const sessionCount = sessionInfo?.sessionCount || 0;
      const hasAnalysisData = !!latestReport || hasBackendData;

      // 나이 계산
      const birthYear = user.birthDate ? parseInt(user.birthDate.split('-')[0]) : null;
      const age = birthYear ? new Date().getFullYear() - birthYear : null;

      return {
        ...user,
        healthScore: latestReport ? Math.round(latestReport.report.mindScore.score) : undefined,
        healthStatus: latestReport ? latestReport.report.decisionLabel : undefined,
        stressLevel: latestReport ? latestReport.report.stressAnalysis.category : undefined,
        lastAnalysisDate: latestReport ? latestReport.analysisDate : undefined,
        age: age || undefined,
        hasAnalysisData,
        hasBackendData,
        sessionCount,
      };
    });

    // 정렬: 분석 데이터 있음 우선 -> 이름순 (가나다)
    return mappedUsers.sort((a, b) => {
      if (a.hasAnalysisData !== b.hasAnalysisData) {
        return a.hasAnalysisData ? -1 : 1; // true(있음)가 먼저 오도록 (-1)
      }
      return a.name.localeCompare(b.name, 'ko-KR');
    });
  }, [filteredUsers, allSessionCounts]);

  // 페이지네이션
  const totalPages = Math.ceil(usersWithHealth.length / PAGE_SIZE);
  const paginatedUsers = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    return usersWithHealth.slice(start, end);
  }, [usersWithHealth, currentPage]);

  // 페이지네이션된 사용자 리스트 (이미 usersWithHealth에서 모든 정보가 병합되었으므로 그대로 사용)
  const paginatedUsersWithSessions = paginatedUsers;
  
  // 선택된 사용자 (백엔드 세션 정보 포함)
  const selectedUser = useMemo(() => {
    const userInList = paginatedUsersWithSessions.find(u => u.id === userId) || usersWithHealth.find(u => u.id === userId);
    if (!userInList) return undefined;
    
    // 이 부분은 selectedUser가 다시 계산될 때마다 최신 세션 정보를 반영하기 위해 유지합니다.
    const sessionInfo = allSessionCounts.get(userInList.id);
    return {
      ...userInList,
      hasBackendData: sessionInfo?.hasData || userInList.hasBackendData || false,
      sessionCount: sessionInfo?.sessionCount || userInList.sessionCount || 0,
      hasAnalysisData: userInList.hasAnalysisData || (sessionInfo?.hasData || false),
    };
  }, [paginatedUsersWithSessions, usersWithHealth, userId, allSessionCounts]);


  // 선택된 사용자의 최근 분석 기록 (백엔드 세션 + localStorage 결합)
  const recentAnalyses: RecentAnalysis[] = useMemo(() => {
    if (!selectedUser) return [];

    // 백엔드 세션 데이터 (COMPLETED 상태만)
    const sessions = sessionsData?.data || [];
    const completedSessions = sessions.filter(session => session.status === 'COMPLETED');

    const sessionAnalyses: RecentAnalysis[] = completedSessions.map((session) => ({
      id: session.sessionId,
      sessionId: session.sessionId,
      userId: selectedUser.id,
      userName: selectedUser.name,
      analysisDate: session.endTime || session.startTime,
      filename: `Session_${session.sessionId}`,
      isFromBackend: true,
      session, // 전체 세션 정보 포함
      // localStorage에서 캐시된 분석 결과가 있는지 확인
      report: mentalHealthStore.getReportById(session.sessionId)?.report,
    }));

    // localStorage 데이터 (백엔드 세션 ID와 중복되지 않는 것만)
    const sessionIds = new Set(sessions.map(s => s.sessionId));
    const localReports: RecentAnalysis[] = mentalHealthStore.getAllReports()
      .filter(r => r.userId === selectedUser.id && !sessionIds.has(r.id.toString()))
      .map((r) => ({
        ...r,
        isFromBackend: false,
      }));

    // 합치고 날짜순 정렬
    const combined = [...sessionAnalyses, ...localReports]
      .sort((a, b) => new Date(b.analysisDate).getTime() - new Date(a.analysisDate).getTime())
      .slice(0, 10);

    return combined;
  }, [selectedUser, sessionsData]);

  // 선택된 사용자의 최신 리포트 (첫 번째 분석)
  const latestReport = useMemo(() => {
    return recentAnalyses[0] || null;
  }, [recentAnalyses]);

  // 최신 데이터 캐시 업데이트
  useEffect(() => {
    if (selectedUser && latestReport) {
      setLatestDataCache(prev => ({
        ...prev,
        [selectedUser.id]: {
          healthScore: latestReport.report ? Math.round(latestReport.report.mindScore.score) : 0,
          healthStatus: latestReport.report ? latestReport.report.decisionLabel : 'insufficient_data',
          stressLevel: latestReport.report ? latestReport.report.stressAnalysis.category : 'insufficient_data',
        }
      }));
    }
  }, [selectedUser, latestReport]);

  // 첫 번째 사용자 자동 선택
  useEffect(() => {
    if (userId <= 0 && paginatedUsersWithSessions.length > 0) {
      setUserId(paginatedUsersWithSessions[0].id);
    }
  }, [paginatedUsersWithSessions, userId]);

  // 검색어 변경 시 첫 페이지로
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchText]);

  // URL 파라미터 업데이트
  useEffect(() => {
    const params: any = {};
    if (userId > 0) params.id = userId.toString();
    if (currentPage > 1) params.page = currentPage.toString();
    setSearchParams(params);
  }, [userId, currentPage, setSearchParams]);



  // 분석 완료된 회원 수 (백엔드 세션 + 로컬 스토리지)
  const analyzedUsersCount = useMemo(() => {
    // 1. 로컬 스토리지에서 분석 완료된 회원 ID 확인
    const allReports = mentalHealthStore.getAllReports();
    const localAnalyzedUserIds = new Set(allReports.map(r => r.userId));

    // 2. 백엔드 세션에서 분석 완료된 회원 ID 확인
    const backendAnalyzedUserIds = new Set<number>();
    if (allSessionCounts.size > 0) {
      allSessionCounts.forEach((info) => {
        if (info.hasData) {
          backendAnalyzedUserIds.add(info.userId);
        }
      });
    }

    // 3. 두 집합의 합집합 크기 반환
    const combinedAnalyzedUserIds = new Set([...localAnalyzedUserIds, ...backendAnalyzedUserIds]);
    
    // filteredUsers가 있을 때만 해당 범위 내에서 카운트 (검색 시 필터링된 결과 내에서 카운트되는지 확인 필요)
    // 전체 사용자 기준이라면 아래 필터링 제거
    if (filteredUsers) {
        const filteredUserIds = new Set(filteredUsers.map(u => u.id));
        let count = 0;
        combinedAnalyzedUserIds.forEach(id => {
            if (filteredUserIds.has(id)) count++;
        });
        return count;
    }

    return combinedAnalyzedUserIds.size;
  }, [allSessionCounts, filteredUsers]);

  // 세션 데이터로부터 리포트 로드
  const loadReportFromSession = async (sessionId: string) => {
    if (!userId || !selectedUser) {
      return null;
    }

    try {
      // localStorage에서 먼저 확인
      const cachedReport = mentalHealthStore.getReportById(sessionId);
      if (cachedReport) {
        return cachedReport;
      }

      // 백엔드에서 BCG 데이터 가져오기
      const bcgDataResponse = await apiGetSessionBCGData(userId, sessionId);

      if (!bcgDataResponse.data) {
        return null;
      }

      // BCG 데이터 변환 및 분석
      const bcgData = bcgDataResponse.data.bcgData;
      const csvData = convertBCGDataToCSVFormat(bcgData);

      const params: AnalysisParams = {
        windowSec: 60,
        strideSec: 15,
        mode: 'absolute',
        calibrationPairs: [],
      };

      const report = analyzeBCGData(csvData, params);

      if (!report) {
        return null;
      }

      // localStorage에 캐싱 (sessionId를 ID로 사용)
      const storedReport = mentalHealthStore.saveReport(
        `Session_${sessionId}`,
        report,
        userId,
        selectedUser.name,
        sessionId // customId로 sessionId 전달
      );

      return storedReport;
    } catch (error) {
      console.error('리포트 로드 실패:', error);
      return null;
    }
  };

  // BCG 데이터 변환 함수
  const convertBCGDataToCSVFormat = (bcgData: number[], chunkSize: number = 256) => {
    const csvRows: { deviceTime: string, bcgMerge: string }[] = [];
    const totalChunks = Math.ceil(bcgData.length / chunkSize);

    for (let i = 0; i < totalChunks; i++) {
      const start = i * chunkSize;
      const end = Math.min(start + chunkSize, bcgData.length);
      const chunkData = bcgData.slice(start, end);

      csvRows.push({
        deviceTime: new Date(Date.now() + i * 1000).toISOString(),
        bcgMerge: JSON.stringify({ data: chunkData }),
      });
    }

    return csvRows;
  };

  const handleUploadSuccess = () => {
    // 업로드 성공 후 세션 목록 새로고침
    refetchSessions();
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  // 선택된 회원의 최신 리포트 상세 페이지로 이동
  const handleViewDetailReport = async () => {
    if (!latestReport) return;

    // 백엔드 세션인 경우 먼저 로드
    if (latestReport.isFromBackend && 'sessionId' in latestReport && latestReport.sessionId) {
      await loadReportFromSession(latestReport.sessionId);
    }

    navigate(`/mental-health/reports/${String(latestReport.id)}`);
  };

  // 분석 기록 클릭 핸들러
  const handleAnalysisClick = async (analysis: RecentAnalysis) => {
    if (analysis.isFromBackend && analysis.sessionId) {
      const loadedReport = await loadReportFromSession(analysis.sessionId);

      if (!loadedReport) {
        return;
      }
    }

    navigate(`/mental-health/reports/${String(analysis.id)}`);
  };

  // 선택된 회원의 리포트 목록 페이지로 이동 (필터링)
  const handleViewAllReports = () => {
    if (!selectedUser) return;
    // userId를 쿼리 파라미터로 전달하여 해당 회원 리포트만 표시
    navigate(`/mental-health/reports?userId=${selectedUser.id}`);
  };

  const getStatusColor = (status?: string) => {
    if (!status) return { bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-300', dot: 'bg-gray-500' };

    // 영어 키로 직접 비교
    switch (status) {
      case 'healthy':
      case '건강':
        return { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-300', dot: 'bg-emerald-500' };
      case 'normal':
      case '보통':
        return { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-300', dot: 'bg-blue-500' };
      case 'caution':
      case '주의':
        return { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-300', dot: 'bg-amber-500' };
      case 'risk':
      case '위험':
        return { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-300', dot: 'bg-red-500' };
      case 'insufficient_data':
      default:
        return { bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-300', dot: 'bg-gray-500' };
    }
  };

  const getStressEmoji = (stress?: string) => {
    if (!stress) return '⏳';

    // 영어 키로 직접 비교
    switch (stress) {
      case 'low':
      case '낮음': return '😌';
      case 'normal':
      case '보통': return '😐';
      case 'high':
      case 'very_high':
      case '높음': return '😰';
      default: return '⏳';
    }
  };

  // 선택된 사용자의 실제 차트 데이터 생성 (실제 데이터만)
  const chartData = useMemo(() => {
    if (!selectedUser || !selectedUser.hasAnalysisData) {
        return {
            labels: [],
            datasets: []
        };
    }

    // recentAnalyses 데이터를 사용하여 차트 데이터 생성
    const userReports = [...recentAnalyses] // 복사본을 만들어 정렬
        .sort((a, b) => new Date(a.analysisDate).getTime() - new Date(b.analysisDate).getTime())
        .slice(-7);

    if (userReports.length === 0) {
        return {
            labels: [],
            datasets: []
        };
    }

    const locale = i18n.language === 'ja' ? 'ja-JP' : i18n.language === 'en' ? 'en-US' : 'ko-KR';
    return {
        labels: userReports.map(r => new Date(r.analysisDate).toLocaleDateString(locale, { month: 'short', day: 'numeric' })),
        datasets: [
            {
                label: '건강 점수',
                data: userReports.map(r => (r.report ? Math.round(r.report.mindScore.score) : 0)),
                borderColor: 'rgba(16, 185, 129, 1)',
                backgroundColor: 'rgba(16, 185, 129, 0.1)',
                fill: true,
                tension: 0.4,
            },
        ],
    };
  }, [selectedUser, recentAnalyses, i18n.language]);


  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
      }
    },
    scales: {
      y: {
        min: 0,
        max: 100,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        }
      },
      x: {
        grid: {
          display: false,
        }
      }
    },
  };
  
  const headerHealthStatus = latestReport?.report ? latestReport.report.decisionLabel : 'insufficient_data';
  const headerStressLevel = latestReport?.report ? latestReport.report.stressAnalysis.category : 'insufficient_data';
  const headerHealthScore = latestReport?.report ? Math.round(latestReport.report.mindScore.score) : 0;


  return (
    <PageConfig showTopNav={true} showSideNav={true}>
      {!isLoading && (
        <div className="grid grid-cols-[340px_auto] h-[calc(100vh-60px)]">
          {/* 좌측: 회원 리스트 */}
          <div className="bg-white overflow-y-auto flex flex-col">
            {/* 헤더 */}
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-emerald-600 to-teal-600">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center">
                <span className="text-2xl mr-2">💚</span>
                {t('mentalHealth.title')}
              </h2>
              <SearchBar
                inputParams={{
                  placeholder: t('mentalHealth.search.placeholder')
                }}
                onChanged={setSearch}
              />
            </div>

            {/* 통계 요약 */}
            <div className="p-4 bg-emerald-50 border-b border-emerald-100">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-emerald-600">
                    {usersWithHealth.length}
                  </div>
                  <div className="text-xs text-gray-600">{t('mentalHealth.stats.totalMembers')}</div>
                </div>
                <div className="bg-white rounded-lg p-3 text-center">
                  {isAllSessionCountsLoading ? (
                    <div className="flex justify-center items-center h-8 mb-1">
                      <IcLoading className="w-6 h-6 animate-spin text-blue-600" />
                    </div>
                  ) : (
                    <div className="text-2xl font-bold text-blue-600">
                      {analyzedUsersCount}
                    </div>
                  )}
                  <div className="text-xs text-gray-600">{t('mentalHealth.stats.analyzed')}</div>
                </div>
              </div>
            </div>

            {/* 회원 리스트 */}
            <div className="flex-1 overflow-y-auto">
              {isEmptyArray(paginatedUsers) && (
                <div className="flex flex-col items-center justify-center h-full text-gray-400">
                  <div className="text-6xl mb-4">🔍</div>
                  <p className="text-sm">
                    {debouncedSearchText ? t('mentalHealth.empty.search') : t('mentalHealth.empty.noMembers')}
                  </p>
                </div>
              )}

              {isNotEmptyArray(paginatedUsersWithSessions) && (
                <div className="divide-y divide-gray-100">
                  {paginatedUsersWithSessions.map((user: UserHealthData) => {
                    const cachedData = latestDataCache[user.id];
                    const displayData = cachedData ? {
                      healthScore: cachedData.healthScore,
                      healthStatus: cachedData.healthStatus,
                      stressLevel: cachedData.stressLevel,
                    } : {
                      healthScore: user.healthScore,
                      healthStatus: user.healthStatus,
                      stressLevel: user.stressLevel,
                    };

                    const statusColors = getStatusColor(displayData.healthStatus);
                    const genderText = user.gender === 1 ? t('common.gender.male') : user.gender === 2 ? t('common.gender.female') : t('common.gender.none');
                    const isSelected = selectedUser?.id === user.id;

                    return (
                      <div
                        key={user.id}
                        onClick={() => setUserId(user.id)}
                        className={`p-4 cursor-pointer transition-all ${isSelected
                            ? 'bg-emerald-50 border-l-4 border-emerald-500'
                            : 'hover:bg-gray-50 border-l-4 border-transparent'
                          }`}
                      >
                        <div className="flex items-center space-x-3">
                          {/* 상태 표시등 */}
                          <div className={`w-3 h-3 rounded-full ${statusColors.dot} ${isSelected ? 'animate-pulse' : ''}`}></div>

                          {/* 아바타 */}
                          <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${user.gender === 2 ? 'from-pink-400 to-rose-400' : 'from-blue-400 to-cyan-400'
                            } flex items-center justify-center text-white font-bold text-lg flex-shrink-0`}>
                            {user.name.charAt(0)}
                          </div>

                          {/* 정보 */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center space-x-2">
                                <span className="font-bold text-gray-900 truncate">{user.name}</span>
                                {user.hasBackendData && (
                                  <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-600 font-medium" title="백엔드 데이터 있음">
                                    ☁️
                                  </span>
                                )}
                              </div>
                              {user.hasAnalysisData ? (
                                <span className={`text-xs px-2 py-1 rounded-full ${statusColors.bg} ${statusColors.text} font-semibold`}>
                                  {displayData.healthStatus ? t(`mentalHealth.status.${displayData.healthStatus}`) : t('mentalHealth.status.pending')}
                                </span>
                              ) : (
                                <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-500 font-semibold">
                                  {t('mentalHealth.status.pending')}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center space-x-2 text-xs text-gray-600">
                              <span>{genderText}</span>
                              {user.age && (
                                <>
                                  <span>•</span>
                                  <span>{t('dashboard.notification.age', { count: user.age })}</span>
                                </>
                              )}
                            </div>
                            <div className="flex items-center justify-between mt-1">
                              <span className="text-xs text-gray-500">
                                {getStressEmoji(displayData.stressLevel)} {displayData.stressLevel ? t(`mentalHealth.stress.${displayData.stressLevel}`) : t('mentalHealth.status.pending')}
                              </span>
                              {user.hasAnalysisData && (
                                <span className="text-xs font-semibold text-emerald-600">
                                  {displayData.healthScore}{t('mentalHealth.score.points')}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* 페이지네이션 */}
            {totalPages > 1 && (
              <div className="p-4 border-t border-gray-200">
                <Pagination
                  page_size={PAGE_SIZE}
                  current_page={currentPage}
                  total_page={totalPages}
                  fetchPage={handlePageChange}
                />
              </div>
            )}
          </div>

          {/* 우측: 상세 정보 */}
          <div className="overflow-y-auto bg-gray-50">
            {selectedUser ? (
              (selectedUser.hasAnalysisData || recentAnalyses.length > 0) ? (
                // 분석 데이터가 있는 경우 (로컬 또는 백엔드)
                <div className="p-8 space-y-6">
                  {/* 헤더 */}
                  <div className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-3xl p-8 text-white shadow-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-6">
                        <div className={`w-24 h-24 rounded-full bg-gradient-to-br ${selectedUser.gender === 2 ? 'from-pink-300 to-rose-300' : 'from-blue-300 to-cyan-300'
                          } flex items-center justify-center text-4xl font-black border-4 border-white shadow-xl`}>
                          {selectedUser.name.charAt(0)}
                        </div>
                        <div>
                          <h1 className="text-4xl font-black mb-2">{selectedUser.name}</h1>
                          <div className="flex items-center space-x-4 text-emerald-100">
                            <span>{selectedUser.gender === 1 ? t('common.gender.male') : selectedUser.gender === 2 ? t('common.gender.female') : t('common.gender.none')}</span>
                            {selectedUser.age && (
                              <>
                                <span>•</span>
                                <span>{t('dashboard.notification.age', { count: selectedUser.age })}</span>
                              </>
                            )}
                            {latestReport && (
                              <>
                                <span>•</span>
                                <span>{t('mentalHealth.lastAnalysis')}: {new Date(latestReport.analysisDate).toLocaleDateString(i18n.language === 'ja' ? 'ja-JP' : i18n.language === 'en' ? 'en-US' : 'ko-KR')}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-6xl font-black mb-2">{headerHealthScore}</div>
                        <div className="text-lg opacity-90">{t('mentalHealth.score.health')}</div>
                      </div>
                    </div>
                  </div>

                  {/* 주요 지표 */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* 상태 */}
                    <div className="bg-white rounded-2xl p-6 shadow-md border-2 border-gray-100">
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-gray-600 font-medium">{t('mentalHealth.currentStatus')}</span>
                        <span className="text-3xl">✅</span>
                      </div>
                      <div className={`text-2xl font-bold ${getStatusColor(headerHealthStatus).text}`}>
                        {headerHealthStatus ? t(`mentalHealth.status.${headerHealthStatus}`) : t('mentalHealth.status.pending')}
                      </div>
                      <div className="mt-2 text-sm text-gray-500">
                        {t('mentalHealth.currentStatus.desc')}
                      </div>
                    </div>

                    {/* 스트레스 */}
                    <div className="bg-white rounded-2xl p-6 shadow-md border-2 border-gray-100">
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-gray-600 font-medium">{t('mentalHealth.stress.title')}</span>
                        <span className="text-3xl">{getStressEmoji(headerStressLevel)}</span>
                      </div>
                      <div className="text-2xl font-bold text-gray-900">
                        {headerStressLevel ? t(`mentalHealth.stress.${headerStressLevel}`) : t('mentalHealth.status.pending')}
                      </div>
                      <div className="mt-2 text-sm text-gray-500">
                        {t('mentalHealth.stress.desc')}
                      </div>
                    </div>
                  </div>

                  {/* 건강 추이 차트 */}
                  <div className="bg-white rounded-3xl p-8 shadow-md border border-gray-100">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900 flex items-center">
                          <span className="text-3xl mr-3">📊</span>
                          {t('mentalHealth.chart.trend')}
                        </h3>
                        <p className="text-gray-600 text-sm mt-1">{t('mentalHealth.chart.trend.desc')}</p>
                      </div>
                      {chartData.labels.length > 0 && (
                        <BorderButton
                          text={t('mentalHealth.button.viewDetailAnalysis')}
                          onClick={handleViewDetailReport}
                        />
                      )}
                    </div>
                    {chartData.labels.length > 0 ? (
                      <div className="h-80">
                        <Line data={chartData} options={chartOptions} />
                      </div>
                    ) : (
                      <div className="h-80 flex items-center justify-center text-gray-400">
                        <div className="text-center">
                          <div className="text-5xl mb-4">📈</div>
                          <p>{t('mentalHealth.chart.insufficient')}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* HRV 기반 증상 예측 */}
                  <div className="bg-white rounded-3xl p-8 shadow-md border border-gray-100">
                    <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                      <span className="text-3xl mr-3">🏥</span>
                      {t('mentalHealth.symptom.prediction')}
                    </h3>
                    {latestReport?.report?.symptomPredictions ? (
                      <>
                        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                          <SymptomPredictionCard prediction={latestReport.report.symptomPredictions.stress} />
                          <SymptomPredictionCard prediction={latestReport.report.symptomPredictions.cardiovascular} />
                          <SymptomPredictionCard prediction={latestReport.report.symptomPredictions.breathing} />
                          <SymptomPredictionCard prediction={latestReport.report.symptomPredictions.depression} />
                          <SymptomPredictionCard prediction={latestReport.report.symptomPredictions.pain} />
                          <SymptomPredictionCard prediction={latestReport.report.symptomPredictions.bloodPressure} />
                        </div>
                        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-4">
                          <p className="text-sm text-blue-800" dangerouslySetInnerHTML={{ __html: t('mentalHealth.symptom.disclaimer') }} />
                        </div>
                      </>
                    ) : (
                      <div className="text-center text-gray-400 py-12">
                        <div className="text-5xl mb-4">🏥</div>
                        <p>{t('mentalHealth.symptom.noData')}</p>
                      </div>
                    )}
                  </div>

                  {/* 최근 활동 */}
                  <div className="bg-white rounded-3xl p-8 shadow-md border border-gray-100">
                    <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                      <span className="text-3xl mr-3">📝</span>
                      {t('mentalHealth.recentAnalysis')}
                    </h3>
                    {recentAnalyses.length > 0 ? (
                      <>
                        <div className="space-y-3">
                          {recentAnalyses.map((analysis) => {
                            const analysisDate = new Date(analysis.analysisDate);
                            return (
                              <div
                                key={analysis.id.toString()}
                                className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors cursor-pointer"
                                onClick={() => handleAnalysisClick(analysis)}
                              >
                                <div className="flex items-center space-x-4">
                                  <div className="text-2xl">{analysis.isFromBackend ? '☁️' : '📅'}</div>
                                  <div>
                                    <div className="font-semibold text-gray-900">
                                      {analysisDate.toLocaleDateString(i18n.language === 'ja' ? 'ja-JP' : i18n.language === 'en' ? 'en-US' : 'ko-KR')}
                                    </div>
                                    <div className="text-sm text-gray-600">
                                      {analysisDate.toLocaleTimeString(i18n.language === 'ja' ? 'ja-JP' : i18n.language === 'en' ? 'en-US' : 'ko-KR', { hour: '2-digit', minute: '2-digit' })}
                                      {analysis.isFromBackend && <span className="ml-2 text-blue-600">• 서버</span>}
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center space-x-4">
                                  {analysis.report ? (
                                    <>
                                      <div className="text-right">
                                        <div className="text-2xl font-bold text-gray-900">{Math.round(analysis.report.mindScore.score)}</div>
                                        <div className="text-xs text-gray-600">{t('mentalHealth.score.points')}</div>
                                      </div>
                                      <div className={`px-3 py-1 rounded-lg text-sm font-semibold ${getStatusColor(analysis.report.decisionLabel).bg} ${getStatusColor(analysis.report.decisionLabel).text}`}>
                                        {t(`mentalHealth.status.${analysis.report.decisionLabel}`)}
                                      </div>
                                    </>
                                  ) : (
                                    <div className="text-sm text-gray-500">
                                      {t('mentalHealth.clickToLoad')}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <div className="mt-4">
                          <BorderButton
                            text={t('mentalHealth.recentAnalysis.viewAll')}
                            onClick={handleViewAllReports}
                            className="w-full"
                          />
                        </div>
                      </>
                    ) : (
                      <div className="text-center text-gray-400 py-12">
                        <div className="text-5xl mb-4">📋</div>
                        <p>{t('mentalHealth.recentAnalysis.empty')}</p>
                      </div>
                    )}
                  </div>

                  {/* 액션 버튼들 */}
                  <div className="grid grid-cols-2 gap-4">
                    <SolidButton
                      text={t('mentalHealth.button.viewDetailReport')}
                      onClick={handleViewDetailReport}
                      className="bg-emerald-600 hover:bg-emerald-700 py-4"
                    />
                    <BorderButton
                      text={t('mentalHealth.button.newAnalysis')}
                      onClick={() => sessionModalRef.current?.show()}
                      className="border-2 border-emerald-600 text-emerald-600 hover:bg-emerald-50 py-4"
                    />
                  </div>
                </div>
              ) : (
                // 분석 데이터가 없는 경우
                <div className="flex flex-col items-center justify-center h-full text-gray-400 p-8">
                  <div className="bg-white rounded-3xl p-12 shadow-lg text-center max-w-md">
                    <div className="text-8xl mb-6">📁</div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('mentalHealth.noData.title')}</h3>
                    <p className="text-gray-600 mb-2">
                      {t('mentalHealth.noData.desc1', { name: selectedUser.name })}
                    </p>
                    <p className="text-gray-600 mb-8">
                      {t('mentalHealth.noData.desc2')}
                    </p>

                    {/* 회원 정보 */}
                    <div className="bg-gray-50 rounded-xl p-4 mb-8 text-left">
                      <div className="flex items-center space-x-4 mb-4">
                        <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${selectedUser.gender === 2 ? 'from-pink-400 to-rose-400' : 'from-blue-400 to-cyan-400'
                          } flex items-center justify-center text-white font-bold text-2xl`}>
                          {selectedUser.name.charAt(0)}
                        </div>
                        <div>
                          <div className="font-bold text-lg text-gray-900">{selectedUser.name}</div>
                          <div className="text-sm text-gray-600">
                            {selectedUser.gender === 1 ? t('common.gender.male') : selectedUser.gender === 2 ? t('common.gender.female') : t('common.gender.none')}
                            {selectedUser.age && ` • ${t('dashboard.notification.age', { count: selectedUser.age })}`}
                          </div>
                        </div>
                      </div>
                    </div>

                    <SolidButton
                      text={t('mentalHealth.button.uploadBCG')}
                      onClick={() => sessionModalRef.current?.show()}
                      className="w-full py-4 text-lg bg-emerald-600 hover:bg-emerald-700"
                    />
                  </div>
                </div>
              )
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-gray-400">
                <div className="text-8xl mb-6">👤</div>
                <h3 className="text-2xl font-bold text-gray-600 mb-2">{t('mentalHealth.empty.selectMember')}</h3>
                <p className="text-gray-500">{t('mentalHealth.empty.selectMember.desc')}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* BCG 파일 업로드 모달 (기존 CSV 업로드) */}
      <BCGUploadModal
        ref={uploadModalRef}
        userId={selectedUser?.id}
        userName={selectedUser?.name}
        onSuccess={handleUploadSuccess}
      />

      {/* BCG 세션 모달 (실시간 데이터 수집) */}
      <BCGSessionModal
        ref={sessionModalRef}
        userId={selectedUser?.id}
        userName={selectedUser?.name}
        onSuccess={handleUploadSuccess}
      />
    </PageConfig>
  );
};

export default MentalHealthDashboard;
